import { useState, useEffect } from "react";
import axios from "axios";
import { ShieldCheck, Download, Send, Save, Loader2 } from "lucide-react";
import { PROFESSION_IDS, PROF_T } from "../content/professions";
import { LevelRows } from "./ProfessionPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const T = PROF_T.sk;
const EMPTY = { app_id: "", name: "", email: "", number: "", professions: [], prof_levels: {}, profession_other: "", description: "" };
const inp = "w-full bg-transparent border-b border-white/20 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]";

export default function OnlineCertTemplate({ token, lang }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [apps, setApps] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  useEffect(() => {
    axios.get(`${API}/admin/applications`, auth).then((r) => setApps((r.data || []).filter((a) => a.track === "verification"))).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (id) => {
    const a = apps.find((x) => x.id === id);
    setMsg("");
    setF(a ? { app_id: a.id, name: a.subject_name || a.applicant_name || "", email: a.applicant_email || "", number: a.evidence_number || "",
      professions: a.professions || [], prof_levels: a.prof_levels || {}, profession_other: a.profession_other || "", description: a.specialization || "" } : EMPTY);
  };
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const toggle = (id) => setF((p) => ({ ...p, professions: p.professions.includes(id) ? p.professions.filter((x) => x !== id) : [...p.professions, id] }));
  const body = (mode) => ({ lang, mode, name: f.name.trim(), email: f.email.trim(), number: f.number.trim(), professions: f.professions,
    prof_levels: f.prof_levels, profession_other: f.profession_other, description: f.description.trim() });
  const err = (e) => setMsg(e?.response?.data?.detail || "Akcia zlyhala.");

  const download = async () => {
    setBusy("pdf"); setMsg("");
    try {
      const r = await axios.post(`${API}/admin/online-cert`, body("download"), { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const el = document.createElement("a");
      el.href = url; el.download = `potvrdenie-preverenie-online-${lang}.pdf`; document.body.appendChild(el); el.click(); el.remove();
    } catch (e) {
      let d = ""; try { d = JSON.parse(await e.response.data.text()).detail; } catch { /* ignore */ }
      setMsg(d || "Vytvorenie PDF zlyhalo.");
    } finally { setBusy(""); }
  };
  const send = async () => {
    if (!window.confirm(`Odoslať potvrdenie na ${f.email}?`)) return;
    setBusy("send"); setMsg("");
    try { await axios.post(`${API}/admin/online-cert`, body("email"), auth); setMsg(`Odoslané na ${f.email}.`); } catch (e) { err(e); } finally { setBusy(""); }
  };
  const save = async () => {
    if (!f.professions.length || f.professions.some((p) => !f.prof_levels[p])) { setMsg("Pri každej profesii vyberte úroveň."); return; }
    setBusy("save"); setMsg("");
    try {
      await axios.put(`${API}/admin/applications/${f.app_id}`, { professions: f.professions, prof_levels: f.prof_levels, profession_other: f.profession_other }, auth);
      setApps((p) => p.map((a) => (a.id === f.app_id ? { ...a, professions: f.professions, prof_levels: f.prof_levels, profession_other: f.profession_other } : a)));
      setMsg("Profesie a úrovne uložené do žiadosti.");
    } catch (e) { err(e); } finally { setBusy(""); }
  };
  const btn = "inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-40";

  return (
    <div className="border border-[#D4AF37]/30 bg-[#0a0a0a] p-6 mb-12" data-testid="online-cert-template">
      <h3 className="font-display text-xl mb-1 flex items-center gap-2"><ShieldCheck size={18} className="text-[#D4AF37]" /> Potvrdenie o preverení online</h3>
      <p className="text-white/40 text-sm mb-5">Vyberte žiadosť o preverenie — údaje sa predvyplnia z toho, čo zadal uchádzač. Profesie a úrovne môžete zmeniť len Vy. Jazyk dokumentu: {lang.toUpperCase()}.</p>
      <select value={f.app_id} onChange={(e) => pick(e.target.value)} data-testid="online-cert-app" className="w-full bg-black border border-white/15 text-white text-sm px-3 py-2.5 mb-5">
        <option value="">— vyplniť ručne / vybrať žiadosť —</option>
        {apps.map((a) => <option key={a.id} value={a.id}>{a.subject_name || a.applicant_name} · {a.evidence_number || "bez čísla"} · {a.applicant_email}</option>)}
      </select>
      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        <input className={inp} placeholder="Meno a priezvisko" value={f.name} onChange={set("name")} data-testid="online-cert-name" />
        <input className={inp} placeholder="E-mail" value={f.email} onChange={set("email")} data-testid="online-cert-email" />
        <input className={inp} placeholder="Sa.Eko.Verified. 2026-XXXX" value={f.number} onChange={set("number")} data-testid="online-cert-number" />
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {PROFESSION_IDS.map((id) => (
          <button type="button" key={id} onClick={() => toggle(id)} data-testid={`online-cert-prof-${id}`}
            className={`px-3 py-1.5 text-xs border transition-colors ${f.professions.includes(id) ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F2E3C6]" : "border-white/15 text-white/55 hover:border-[#D4AF37]/50"}`}>{T.names[id]}</button>
        ))}
      </div>
      {f.professions.includes("other") && <input className={`${inp} mb-4`} placeholder={T.other} value={f.profession_other} onChange={set("profession_other")} data-testid="online-cert-other" />}
      <LevelRows T={T} sel={f.professions} levels={f.prof_levels} other={f.profession_other} testid="online-cert-level"
        onLevel={(id, lv) => setF((p) => ({ ...p, prof_levels: { ...p.prof_levels, [id]: lv } }))} />
      <textarea className={`${inp} mt-5`} rows={2} placeholder="Rozsah preverenia (popis práce)" value={f.description} onChange={set("description")} data-testid="online-cert-desc" />
      <div className="flex flex-wrap gap-2 mt-5">
        <button className={btn} onClick={download} disabled={!!busy} data-testid="online-cert-download">{busy === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Stiahnuť PDF</button>
        <button className={btn} onClick={send} disabled={!!busy || !f.email} data-testid="online-cert-send">{busy === "send" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Odoslať e-mailom</button>
        {f.app_id && <button className={btn} onClick={save} disabled={!!busy} data-testid="online-cert-save">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložiť profesie a úrovne do žiadosti</button>}
      </div>
      {msg && <p className="text-[#D4AF37] text-sm mt-3" data-testid="online-cert-msg">{msg}</p>}
    </div>
  );
}
